import { analects } from "./analects";
import { hash, seededShuffle } from "./rng";
import { cleanTranslation, stripNoteMarks, truncate } from "./text";
import type { Analect } from "./types";

/**
 * Derived questions for passages the authored bank does not reach.
 *
 * The previous release generated these by asking "which of these four
 * translations belongs to this passage", with the other three drawn from
 * unrelated passages — answerable by looking for shared characters, with no
 * understanding required. Two better sources are used instead:
 *
 *  1. 楊伯峻's own word-level glosses (`X——…`), present on 388 passages. The
 *     scholarly answer is already in the notes, and the notes frequently name
 *     the reading they reject, which makes an honest 誤解識別 distractor.
 *  2. Minimal-pair mutations of the correct translation — negation flips,
 *     clause swaps, comparative reversals. The distractor is then the *same*
 *     sentence with its meaning changed, so surface overlap is useless and the
 *     student has to read.
 */

const NOTE_SPLIT = /[⑴-⒇①-⑳]/;
const PHONETIC_ONLY = /^(音[^，。]{0,6}[，,]?\s*)?[A-Za-zàáâäǎāèéêëěēìíîïǐīòóôöǒōùúûüǔūǖǘǚǜ]+[。，]?$/;
const CROSS_REF_ONLY = /^(見|见|參|参)[^。]{0,20}[。]?$/;

export interface Gloss {
  analectId: number;
  term: string;
  /** the reading 楊伯峻 adopts, condensed */
  reading: string;
  /** a reading the note explicitly rejects, when it names one */
  rejected?: string;
}

/** `一般解释为"尊敬"，不妥` / `朱熹…解为"时常"` / `旧注说："…"` */
const REJECT_PATTERNS: RegExp[] = [
  /一般(?:人)?(?:把[^，。]{1,8})?解(?:釋|释|为|為)(?:[为為])?[“"「]([^”"」]{1,18})[”"」][^。]{0,12}(?:不妥|不確|不确|非是|恐非)/,
  /(?:朱熹|朱子)[^。]{0,24}[把解][^。]{0,6}(?:为|為|作)[“"「]([^”"」]{1,18})[”"」]/,
  /(?:舊注|旧注|舊說|旧说)[^。]{0,10}[：:]?[“"「]([^”"」]{1,18})[”"」]/,
];

function condense(body: string): string {
  const cleaned = body
    .replace(/《[^》]{1,30}》/g, "")
    .replace(/[（(][^）)]{0,40}[）)]/g, "")
    .trim();
  const first = cleaned.split(/[。；;]/).map((part) => part.trim()).find((part) => part.length >= 2) ?? cleaned;
  return truncate(first.replace(/^[，,、]/, ""), 22);
}

function usableGloss(term: string, body: string): boolean {
  if (!term || term.length > 6) return false;
  if (body.length < 2) return false;
  if (PHONETIC_ONLY.test(body.trim())) return false;
  if (CROSS_REF_ONLY.test(body.trim())) return false;
  // A gloss is only useful if it actually states a meaning.
  return /(意思|意義|意义|義|义|同[“"「]|即|猶|犹|指|讀|读|作[^，。]{1,6}用|、)/.test(body);
}

function parseGlosses(analect: Analect): Gloss[] {
  const raw = (analect.annotations ?? "").replace(/^注[释釋]：/, "");
  if (!raw.trim()) return [];
  const out: Gloss[] = [];
  for (const segment of raw.split(NOTE_SPLIT)) {
    const match = segment.match(/^\s*([一-鿿]{1,6})\s*——\s*([\s\S]+)$/);
    if (!match) continue;
    const [, term, body] = match;
    if (!usableGloss(term, body)) continue;
    let rejected: string | undefined;
    for (const pattern of REJECT_PATTERNS) {
      const hit = body.match(pattern);
      if (hit?.[1]) {
        rejected = truncate(hit[1], 22);
        break;
      }
    }
    const reading = condense(body);
    if (!reading || reading === rejected) continue;
    out.push({ analectId: analect.id, term, reading, rejected });
  }
  return out;
}

const glossCache = new Map<number, Gloss[]>();

export function glossesOf(analect: Analect): Gloss[] {
  const cached = glossCache.get(analect.id);
  if (cached) return cached;
  const parsed = parseGlosses(analect);
  glossCache.set(analect.id, parsed);
  return parsed;
}

/** Every usable gloss in the book — the distractor pool for 訓詁 questions. */
export const ALL_GLOSSES: Gloss[] = analects.flatMap((analect) => glossesOf(analect));

export function glossDistractors(target: Gloss, seed: number, count: number): string[] {
  const pool = ALL_GLOSSES.filter(
    (gloss) =>
      gloss.term !== target.term &&
      gloss.reading !== target.reading &&
      Math.abs(gloss.reading.length - target.reading.length) <= 8,
  );
  const picked = seededShuffle(pool, seed).slice(0, count * 3);
  const out: string[] = [];
  for (const gloss of picked) {
    if (out.length >= count) break;
    if (out.includes(gloss.reading)) continue;
    out.push(gloss.reading);
  }
  return out;
}

// ---- minimal-pair mutation of a translation ---------------------------------

interface Mutation {
  text: string;
  /** what was changed, so the review panel can say why it is wrong */
  kind: string;
}

const MUTATORS: Array<(text: string) => Mutation | null> = [
  // negation flip — only when unambiguous (exactly one occurrence)
  (text) => {
    const matches = text.match(/不/g);
    if (!matches || matches.length !== 1) return null;
    return { text: text.replace("不", ""), kind: "把否定改成了肯定" };
  },
  (text) => {
    const matches = text.match(/沒有|没有/g);
    if (!matches || matches.length !== 1) return null;
    return { text: text.replace(/沒有|没有/, "都有"), kind: "把「沒有」改成了「都有」" };
  },
  // comparative reversal: A 不如 B  →  B 不如 A
  (text) => {
    const match = text.match(/([^，。；]{2,16})不如([^，。；]{2,16})/);
    if (!match) return null;
    return { text: text.replace(match[0], `${match[2]}不如${match[1]}`), kind: "把比較的兩端顛倒了" };
  },
  // conditional pair swap: X 則 A，Y 則 B  →  X 則 B，Y 則 A
  (text) => {
    const match = text.match(/([^，。]{2,14})[則则]([^，。]{2,14})[，,]([^，。]{2,14})[則则]([^，。]{2,14})/);
    if (!match) return null;
    const [whole, a, b, c, d] = match;
    return { text: text.replace(whole, `${a}則${d}，${c}則${b}`), kind: "把兩組條件與結果配錯了" };
  },
  // priority reversal: 先 A 後 B → 先 B 後 A
  (text) => {
    const match = text.match(/先([^，。]{2,12})[，,]?(?:然後|然后|後|后)([^，。]{2,12})/);
    if (!match) return null;
    return { text: text.replace(match[0], `先${match[2]}，然後${match[1]}`), kind: "把先後次序顛倒了" };
  },
  // universalising a limited claim
  (text) => {
    if (!/[，。]/.test(text) || /一切|所有|凡是|無論|无论/.test(text)) return null;
    const match = text.match(/^([^，。]{4,24})([，。])/);
    if (!match) return null;
    return { text: text.replace(match[0], `凡是${match[1]}都如此${match[2]}`), kind: "把有條件的說法擴大成了無條件的斷言" };
  },
];

export interface MutatedOption {
  text: string;
  why: string;
}

/** Produce up to `count` meaning-changing variants of `text`. */
export function mutateMeaning(text: string, seed: number, count: number): MutatedOption[] {
  const out: MutatedOption[] = [];
  const order = seededShuffle(
    MUTATORS.map((_, index) => index),
    seed,
  );
  for (const index of order) {
    if (out.length >= count) break;
    const mutated = MUTATORS[index](text);
    if (!mutated) continue;
    const normalized = mutated.text.replace(/\s+/g, "");
    if (normalized === text.replace(/\s+/g, "")) continue;
    if (out.some((option) => option.text.replace(/\s+/g, "") === normalized)) continue;
    out.push({ text: mutated.text, why: `這一項${mutated.kind}，與原文的意思不合。` });
  }
  return out;
}

/** Short, quotable gist of a passage's translation — used as a question stem. */
export function gistOf(analect: Analect): string {
  const translation = cleanTranslation(analect.translation)
    .replace(/[（(][^）)]{0,40}[）)]/g, "")
    .replace(/[［\[][^］\]]{0,60}[］\]]/g, "")
    .trim();
  const quoted = translation.match(/[“「]([^”」]{6,120})[”」]/)?.[1];
  return truncate((quoted ?? translation).replace(/[“”「」]/g, "").trim(), 90);
}

export function passageText(analect: Analect): string {
  return stripNoteMarks(analect.text);
}

export function glossSeed(analect: Analect, gate: string, variant: number): number {
  return hash(`${analect.id}:${gate}:v${variant}`);
}
